import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { UploadCloud, FileCheck2, ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";

const statusMap = {
  not_submitted: { label: "Not started", icon: ShieldQuestion, chip: "" },
  submitted:     { label: "Under review", icon: ShieldAlert,   chip: "pf-chip-lav" },
  approved:      { label: "Verified",     icon: ShieldCheck,   chip: "pf-chip-mint" },
  rejected:      { label: "Needs update", icon: ShieldAlert,   chip: "" },
};

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
const MAX_MB = 5;

export default function Verification() {
  const [state, setState] = useState(null);
  const [businessNumber, setBusinessNumber] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/merchant/verification");
      setState(data);
      if (data?.business_number) setBusinessNumber(data.business_number);
    } catch (e) {
      // Best-effort GET on mount; verification page still renders empty
      // state when the user has never submitted. Surface transient network
      // errors for debugging without blocking the UI.
      // eslint-disable-next-line no-console
      console.debug("[verification] load failed:", e?.message || e);
    }
  };

  useEffect(() => { load(); }, []);

  const pickFile = (f) => {
    setError("");
    if (!f) { setFile(null); setPreview(""); return; }
    if (f.size > MAX_MB * 1024 * 1024) return setError(`File too large — max ${MAX_MB} MB.`);
    setFile(f);
    if (f.type.startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) return setError("Please attach a document.");
    if (!businessNumber.trim()) return setError("Business number is required.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("business_number", businessNumber.trim());
      fd.append("document", file);
      const { data } = await api.post("/merchant/verification", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState(data);
      setFile(null);
      setPreview("");
      toast.success("Verification submitted — a Cyannect admin will review shortly.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const status = state?.status || "not_submitted";
  const meta = statusMap[status];
  const Icon = meta.icon;

  return (
    <div data-testid="merchant-verification">
      <div className="pf-chip pf-chip-cyan">Verification</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Business verification</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-xl">
        Submit your business number and one supporting document (business license, incorporation certificate, or GST registration). Verified brands get a trust badge on their listings and faster coupon approvals.
      </p>

      <div className="pf-card p-6 mt-6 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${status === "approved" ? "bg-emerald-100 text-emerald-700" : status === "rejected" ? "bg-red-100 text-red-700" : status === "submitted" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Current status</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="font-display text-2xl text-slate-900">{meta.label}</div>
            <span className={`pf-chip ${meta.chip}`}>{status.replace("_", " ")}</span>
          </div>
          {state?.notes && <p className="text-sm text-red-600 mt-2">{state.notes}</p>}
          {state?.submitted_at && (
            <p className="text-xs text-slate-500 mt-1">Submitted {new Date(state.submitted_at).toLocaleString()}</p>
          )}
        </div>
      </div>

      {status === "approved" ? (
        <div className="pf-card p-6 mt-4 bg-gradient-to-br from-emerald-50 to-cyan-50 border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-800 font-medium">
            <FileCheck2 className="w-5 h-5" /> Your brand is verified.
          </div>
          <p className="text-sm text-slate-700 mt-2">Your coupons will get a "verified" badge on the marketplace. Contact support if you need to update documents.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="pf-card p-6 mt-4 space-y-5" data-testid="verification-form">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Business number *</label>
            <input
              value={businessNumber}
              onChange={(e) => setBusinessNumber(e.target.value)}
              placeholder="e.g. BN 123456789 RC0001"
              className="pf-input w-full px-4 py-3 text-sm font-mono"
              data-testid="verif-business-number"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Supporting document *</label>
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-cyan-300/70 bg-white/40 rounded-2xl p-8 text-center cursor-pointer hover:border-cyan-500 hover:bg-white/60 transition-colors"
              data-testid="verif-dropzone"
            >
              <UploadCloud className="w-8 h-8 mx-auto text-cyan-600" />
              <div className="mt-2 text-sm font-medium text-slate-900">
                {file ? file.name : "Click or drop a file"}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {file ? `${(file.size / 1024).toFixed(0)} KB` : `PDF, PNG, JPG or WEBP · Max ${MAX_MB} MB`}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="verif-file-input"
              />
            </div>
            {preview && (
              <img src={preview} alt="Preview" className="mt-3 rounded-xl border border-white/80 max-h-64 object-contain bg-white" data-logo="1" />
            )}
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50/70 border border-red-200 rounded-xl px-4 py-2" data-testid="verif-error">{error}</div>}

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Your document is stored securely and only visible to Cyannect admins.</p>
            <button type="submit" disabled={loading} className="pf-btn-primary px-5 py-2.5 rounded-full font-semibold text-sm" data-testid="verif-submit">
              {loading ? "Uploading…" : status === "rejected" ? "Resubmit for review" : "Submit for review"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
